import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  hideConfirmCandidateEligibleJobsPopup,
  INVITE_PROFESSIONALS_TO_JOBS,
  inviteProfessionalsToJobs,
  SHOW_MESSAGE,
} from '~/actions';
import { clearSuccess } from '~/actions/success';
import { createSuccessSelector } from '~/api/selectors';
import { Box, Button, Center, CustomSpinner, Row, Text } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';
import { useInfiniteEligibleJobs } from '~/domain/EligibleJobs';
import { useAppSelector, useLoading } from '~/hooks';
import ClosePopupIcon from '~/images/ClosePopupIcon.svg';
import defaultProfile from '~/images/HY_tooth.png';
import { routes } from '~/routes';
import { Colors } from '~/themes/colors';
import { EligibleJobCard } from './EligibleJobCard';

const inviteAction = [INVITE_PROFESSIONALS_TO_JOBS];
const successSelector = createSuccessSelector(inviteAction);

export const ConfirmCandidateEligibleJobsPopup = ({
  navigateWithoutBlocking,
}: {
  navigateWithoutBlocking: (path: string) => void;
}) => {
  const dispatch = useDispatch();
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { show, candidate, professionalId, officeId, job } = useAppSelector(
    (state) => state.job.confirmCandidateEligibleJobsPopup,
  );
  const invitedSuccessfully = useSelector((state) => successSelector(state));
  const { isLoading: isInviting } = useLoading(inviteAction);

  const { eligibleJobs, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteEligibleJobs({
      xOfficeId: officeId,
      professionalId: professionalId,
      limit: 10,
      enabled: show,
      profession: job?.profession,
    });

  const handleJobSelect = useCallback((jobId: string) => {
    setSelectedJobIds((prev) => {
      if (prev.includes(jobId)) {
        return prev.filter((id) => id !== jobId);
      }
      return [...prev, jobId];
    });
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleInviteClick = () => {
    if (selectedJobIds.length > 0) {
      dispatch(
        inviteProfessionalsToJobs({
          professionalIds: [professionalId],
          jobIds: selectedJobIds,
          officeId,
        }),
      );
    }
  };

  const handlePopupClose = useCallback(
    (route: string) => {
      dispatch(hideConfirmCandidateEligibleJobsPopup());
      setSelectedJobIds([]);
      navigateWithoutBlocking(route);
    },
    [dispatch, navigateWithoutBlocking],
  );

  const onClose = () => {
    handlePopupClose('/dashboard/jobs/scheduled');
  };

  useEffect(() => {
    if (invitedSuccessfully) {
      handlePopupClose('/');
      dispatch(clearSuccess(INVITE_PROFESSIONALS_TO_JOBS));
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          isSuccess: true,
          popupProps: {
            rightButtonText: 'Close',
          },
          title: 'Request(s) Successfully Sent!',
          message: (
            <>
              <b>{candidate?.full_name}</b> has been invited to the selected shift(s). Once they
              accept at the listed rate, {candidate?.full_name.split(' ')[0]} will be confirmed!
            </>
          ),
        },
      });
    }
  }, [invitedSuccessfully, candidate, dispatch, handlePopupClose]);

  const onCardPress = (jobId: string) => {
    navigateWithoutBlocking(routes.jobDetails.replace(':jobId', jobId));
  };

  if (!show || !candidate || !job) {
    return null;
  }

  return (
    <Portal>
      <Box
        className="modal"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      >
        <Box
          className="modal_content"
          style={{
            position: 'relative',
            padding: '40px 30px',
            maxWidth: 700,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}>
            <img alt="close-popup-icon" src={ClosePopupIcon} onClick={onClose} />
          </Box>

          <Box style={{ marginTop: 12, marginBottom: 20, textAlign: 'center' }}>
            {candidate?.profile_image ? (
              <img
                src={candidate.profile_image}
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: 68,
                  objectFit: 'cover',
                }}
                alt="profile_image"
              />
            ) : (
              <Center
                backgroundColor={Colors.secondary_500}
                borderRadius={68}
                width={68}
                height={68}
              >
                <img src={defaultProfile} alt="profile_image" width="40px" height="40px" />
              </Center>
            )}
          </Box>

          <Text fontSize={18} fontWeight="bold">
            Candidate Confirmed for {moment(job?.local_date).format('ddd, MMM D, YYYY')}
          </Text>

          <Text marginTop={16}>
            Would you like to invite <Text bold>{candidate?.full_name}</Text> for other shifts?
          </Text>

          <Box
            ref={scrollContainerRef}
            flex={1}
            marginBottom={24}
            marginTop={16}
            overflowY="auto"
            overflowX="hidden"
            maxHeight="400px"
            scrollbarWidth="thin"
          >
            {isLoading ? (
              <Center
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 40,
                }}
              >
                <CustomSpinner isLoading />
              </Center>
            ) : (
              <Box marginHorizontal={8} marginTop={4} gap={10} paddingBottom={8}>
                {eligibleJobs.map((job) => (
                  <EligibleJobCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJobIds.includes(job.id)}
                    onSelect={handleJobSelect}
                    disabled={isInviting}
                    onCardPress={onCardPress}
                  />
                ))}

                {isFetchingNextPage && (
                  <Center
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 16,
                    }}
                  >
                    <CustomSpinner isLoading size={24} />
                  </Center>
                )}
              </Box>
            )}
          </Box>

          <Row gap={16} justifyContent="center" marginTop={16}>
            <Button
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor: Colors.secondary_500,
              }}
              onClick={onClose}
              text="No Thanks"
              disabled={isInviting}
            />

            <Button
              text={
                selectedJobIds.length > 0
                  ? `Invite for ${selectedJobIds.length} Shift${selectedJobIds.length !== 1 ? 's' : ''}`
                  : 'Invite'
              }
              onClick={handleInviteClick}
              disabled={selectedJobIds.length === 0}
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor:
                  selectedJobIds.length > 0 ? Colors.primary_500 : Colors.primary_200,
              }}
              isLoading={isInviting}
            />
          </Row>
        </Box>
      </Box>
    </Portal>
  );
};
